// services/notificationService.js
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebaseConfig';

/**
 * Create a new notification
 * @param {Object} notificationData - Notification information
 * @returns {Promise} Result object with success status
 */
export const createNotification = async (notificationData) => {
  try {
    const notification = {
      recipientId: notificationData.recipientId,
      recipientType: notificationData.recipientType, // user, hospital, department
      type: notificationData.type, // application, dispute, approval, rejection, reminder
      subType: notificationData.subType, // submitted, approved, rejected, dispute_raised, etc.
      title: notificationData.title,
      message: notificationData.message,
      applicationId: notificationData.applicationId || null,
      disputeId: notificationData.disputeId || null,
      actionUrl: notificationData.actionUrl || null,
      priority: notificationData.priority || 'medium', // low, medium, high, urgent
      read: false,
      readAt: null,
      metadata: notificationData.metadata || {},
      createdAt: Timestamp.now(),
      expiresAt: notificationData.expiresAt || null
    };

    const docRef = await addDoc(collection(db, 'notifications'), notification);
    
    return {
      success: true,
      notificationId: docRef.id,
      message: 'Notification created successfully'
    };
  } catch (error) {
    console.error('Error creating notification:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get notifications for a specific recipient
 * @param {Object} params - Query parameters
 * @returns {Promise} Result with notifications array
 */
export const getNotifications = async (params) => {
  try {
    let q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', params.recipientId),
      orderBy('createdAt', 'desc')
    );

    if (params.limit) {
      q = query(q, limit(params.limit));
    }

    if (params.unreadOnly) {
      q = query(
        collection(db, 'notifications'),
        where('recipientId', '==', params.recipientId),
        where('read', '==', false),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      readAt: doc.data().readAt?.toDate(),
      expiresAt: doc.data().expiresAt?.toDate()
    }));
    
    return {
      success: true,
      notifications
    };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return {
      success: false,
      error: error.message,
      notifications: []
    };
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise} Result object
 */
export const markAsRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), {
      read: true,
      readAt: Timestamp.now()
    });
    
    return {
      success: true,
      message: 'Notification marked as read'
    };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Mark all notifications as read for a recipient
 * @param {string} recipientId - Recipient ID
 * @returns {Promise} Result object
 */
export const markAllAsRead = async (recipientId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', recipientId),
      where('read', '==', false)
    );
    
    const snapshot = await getDocs(q);
    const updatePromises = snapshot.docs.map(docSnapshot => 
      updateDoc(doc(db, 'notifications', docSnapshot.id), {
        read: true,
        readAt: Timestamp.now()
      })
    );
    
    await Promise.all(updatePromises);
    
    return {
      success: true,
      message: 'All notifications marked as read'
    };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get notification counts
 * @param {string} recipientId - Recipient ID
 * @returns {Promise} Result with counts
 */
export const getNotificationCounts = async (recipientId) => {
  try {
    // Get unread count
    const unreadQuery = query(
      collection(db, 'notifications'),
      where('recipientId', '==', recipientId),
      where('read', '==', false)
    );
    
    const unreadSnapshot = await getDocs(unreadQuery);
    
    // Get total count
    const totalQuery = query(
      collection(db, 'notifications'),
      where('recipientId', '==', recipientId)
    );
    
    const totalSnapshot = await getDocs(totalQuery);
    
    return {
      success: true,
      unreadCount: unreadSnapshot.size,
      totalCount: totalSnapshot.size
    };
  } catch (error) {
    console.error('Error getting notification counts:', error);
    return {
      success: false,
      error: error.message,
      unreadCount: 0,
      totalCount: 0
    };
  }
};

/**
 * Delete notifications by a list of IDs (batched)
 * @param {string[]} notificationIds
 */
export const deleteNotificationsByIds = async (notificationIds) => {
  try {
    if (!Array.isArray(notificationIds) || notificationIds.length === 0) {
      return { success: true, deleted: 0 };
    }

    // Firestore batch limit is 500; keep margin
    const MAX_PER_BATCH = 400;
    let deleted = 0;
    for (let i = 0; i < notificationIds.length; i += MAX_PER_BATCH) {
      const slice = notificationIds.slice(i, i + MAX_PER_BATCH);
      const batch = writeBatch(db);
      slice.forEach((id) => {
        batch.delete(doc(db, 'notifications', id));
      });
      await batch.commit();
      deleted += slice.length;
    }

    return { success: true, deleted };
  } catch (error) {
    console.error('Error deleting notifications by ids:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete all notifications for a recipient
 * @param {string} recipientId
 */
export const deleteAllForRecipient = async (recipientId) => {
  try {
    if (!recipientId) return { success: false, error: 'recipientId required' };

    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', recipientId)
    );
    const snapshot = await getDocs(q);
    const ids = snapshot.docs.map((d) => d.id);
    return await deleteNotificationsByIds(ids);
  } catch (error) {
    console.error('Error deleting all notifications for recipient:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create application status notifications
 * @param {string} applicationId - Application ID
 * @param {string} status - New status
 * @param {Object} applicationData - Application data
 * @param {Object} actionBy - User who performed the action
 * @returns {Promise} Result object
 */
export const createApplicationStatusNotification = async (applicationId, status, applicationData, actionBy) => {
  try {
    const notifications = [];
    
    // Notification for hospital
    if (applicationData.hospitalId) {
      notifications.push({
        recipientId: applicationData.hospitalId,
        recipientType: 'hospital',
        type: 'application',
        subType: status,
        title: getApplicationStatusTitle(status),
        message: getApplicationStatusMessage(status, applicationData, actionBy),
        applicationId,
        priority: getPriorityByStatus(status),
        metadata: {
          patientName: applicationData.patient?.name,
          actionBy: actionBy?.userName
        }
      });
    }
    
    // Notification for departments based on status
    const departmentRecipients = getDepartmentRecipients(status);
    departmentRecipients.forEach(dept => {
      notifications.push({
        recipientId: dept,
        recipientType: 'department',
        type: 'application',
        subType: status,
        title: getApplicationStatusTitle(status),
        message: getApplicationStatusMessage(status, applicationData, actionBy),
        applicationId,
        priority: getPriorityByStatus(status),
        metadata: {
          patientName: applicationData.patient?.name,
          actionBy: actionBy?.userName
        }
      });
    });
    
    // Create all notifications
    const createPromises = notifications.map(notification => 
      createNotification(notification)
    );
    
    await Promise.all(createPromises);
    
    return {
      success: true,
      message: 'Application status notifications created'
    };
  } catch (error) {
    console.error('Error creating application status notifications:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Helper functions for notification content
 */
const getApplicationStatusTitle = (status) => {
  const titles = {
    submitted: '📋 Application Submitted',
    under_review: '🔍 Application Under Review',
    medical_review: '🏥 Medical Review Required',
    policy_review: '📋 Policy Review Required',
    financial_review: '💰 Financial Review Required',
    kyc_pending: '🆔 KYC Verification Pending',
    approved: '✅ Application Approved',
    rejected: '❌ Application Rejected',
    kyp_generated: '📄 KYP Letter Generated',
    dispute_pending: '⚠️ Query Pending Resolution',
    on_hold: '⏸️ Application On Hold'
  };
  return titles[status] || '📄 Application Update';
};

const getApplicationStatusMessage = (status, applicationData, actionBy) => {
  const patientName = applicationData.patient?.name || 'Patient';
  const actionByName = actionBy?.userName || 'System';
  
  const messages = {
    submitted: `Application for ${patientName} has been submitted and is awaiting review.`,
    under_review: `Application for ${patientName} is now under review by ${actionByName}.`,
    medical_review: `Medical review is required for ${patientName}'s application.`,
    policy_review: `Policy verification is required for ${patientName}'s application.`,
    financial_review: `Financial review is required for ${patientName}'s application.`,
    kyc_pending: `KYC verification is pending for ${patientName}'s application.`,
    approved: `Application for ${patientName} has been approved by ${actionByName}.`,
    rejected: `Application for ${patientName} has been rejected by ${actionByName}.`,
    kyp_generated: `KYP letter has been generated for ${patientName}'s application.`,
    dispute_pending: `There are pending disputes for ${patientName}'s application that need resolution.`,
    on_hold: `Application for ${patientName} has been put on hold by ${actionByName}.`
  };
  return messages[status] || `Application status updated for ${patientName}.`;
};

const getPriorityByStatus = (status) => {
  const highPriority = ['rejected', 'dispute_pending', 'approved'];
  const mediumPriority = ['under_review', 'kyp_generated'];
  
  if (highPriority.includes(status)) return 'high';
  if (mediumPriority.includes(status)) return 'medium';
  return 'low';
};

const getDepartmentRecipients = (status) => {
  const recipients = {
    submitted: ['medical', 'policy', 'financial'],
    medical_review: ['medical'],
    policy_review: ['policy'],
    financial_review: ['financial'],
    kyc_pending: ['kyc'],
    approved: ['medical', 'policy', 'financial'],
    rejected: ['medical', 'policy', 'financial'],
    kyp_generated: ['medical'],
    dispute_pending: ['medical', 'policy', 'financial']
  };
  return recipients[status] || [];
};

/**
 * Create reminder notifications
 * @param {Object} reminderData - Reminder information
 * @returns {Promise} Result object
 */
export const createReminderNotification = async (reminderData) => {
  try {
    const notification = {
      recipientId: reminderData.recipientId,
      recipientType: reminderData.recipientType,
      type: 'reminder',
      subType: reminderData.reminderType, // pending_review, document_upload, response_required
      title: `⏰ Reminder: ${reminderData.title}`,
      message: reminderData.message,
      applicationId: reminderData.applicationId,
      disputeId: reminderData.disputeId,
      priority: 'medium',
      metadata: reminderData.metadata || {}
    };
    
    return await createNotification(notification);
  } catch (error) {
    console.error('Error creating reminder notification:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Send email using external service
 * @param {Object} params - Email parameters
 * @param {string} params.to - Recipient email address
 * @param {string} params.subject - Email subject
 * @param {string} params.html - Email body (HTML)
 * @param {string} [params.organizationId='SamHealthNew'] - Organization ID
 * @returns {Promise} Result object
 */
export const sendEmail = async ({ to, subject, html, organizationId = 'SamHealthNew' }) => {
  try {
    const callable = httpsCallable(functions, 'sendEmailNotification');
    const result = await callable({ to, subject, html, organizationId });

    if (result.data?.success) {
      console.log(`✓ Email sent successfully to: ${to}`);
      return { success: true };
    }

    const error = result.data?.error || 'Failed to send email';
    console.error(`✗ Failed to send email to ${to}:`, error);
    return { success: false, error };
  } catch (err) {
    console.error('Email send error:', err);
    return { success: false, error: err?.message || 'Failed to send email' };
  }
};

export default {
  createNotification,
  getNotifications,
  markAsRead,
  markAllAsRead,
  getNotificationCounts,
  createApplicationStatusNotification,
  createReminderNotification,
  sendEmail,
  deleteNotificationsByIds,
  deleteAllForRecipient
};
